import * as d3 from 'd3';
import React from 'react';
import { ContainerElement } from 'd3';

import { WebinarPlayerContext } from '$/components/WebinarPlayer/context';

import { useHasChangedShallow } from '$/hooks/useHasChanged';

import { MarkerData } from '../Markers/types';
import { ZoomScaleProviderContext, ZoomScaleProviderState } from './ZoomScaleProviderContext';

export const ZoomScaleProvider: React.FC<{
  domain: [number, number];
  duration?: number;
  startZoomed?: boolean;
  exitDurationSecs?: number;
  onZoomScaleChange?: (domain: [number, number]) => void;
  children?: React.ReactNode;
}> = (props) => {
  const { timelineComponents } = React.useContext(WebinarPlayerContext);

  // domain - waiting to exit room duration tuple
  const { domain, duration, exitDurationSecs } = props;

  // width of timeline container
  const [width, setWidth] = React.useState(10);
  const [widthMeasured, setWidthMeasured] = React.useState(false);

  // normalization - scale domain linearly between 0 to width
  const x = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain(domain) // input
        .range([0, width]), // output
    [width, domain]
  );

  // normalize function for zoom
  // it scales from 1-30
  const zoom = React.useMemo(
    () =>
      d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([1, (width * 30) / (x(domain[1]) ?? 1)]) // isn't x(domain[1]) always equal to width?
        .translateExtent([
          [0, 0],
          [width, 0],
        ])
        .extent([
          [0, 0],
          [width, 0],
        ]),
    [x, width, domain]
  );

  const [zoomTransform, setZoomTransform] =
    React.useState<ZoomScaleProviderState['zoomTransform']>();

  const rescaleAndTranslate = React.useCallback(
    ({ parentEl, newZoomValue, cursorOffset }) => {
      // vertical scroll - zoom in/out
      // Rescale
      const t = d3.zoomTransform(parentEl.node());
      zoom.scaleTo(parentEl, newZoomValue);

      // Retranslate
      const t2 = d3.zoomTransform(parentEl.node()), // reaccess the transform so that the zoom's extents apply
        w = x.range()[1], // My scales reference
        dw = w / t2.k - w / t.k, // The change in width
        xOffset = dw / 2 - dw * (cursorOffset / w);
      //  unt for shift by scale

      // const xOffset = d3.zoomIdentity.rescaleX(x)(newZoomValue);
      zoom.translateBy(parentEl, -xOffset, 0);
    },
    [x, zoom]
  );

  // element ref where zoom applies
  const zoomRef = React.useRef<SVGSVGElement | null>(null);
  const root = React.useRef<d3.Selection<any, any, any, any> | null>(null);
  const zoomRefCallback = React.useCallback(
    (dom: SVGSVGElement | null) => {
      if (!dom) return;

      zoomRef.current = dom;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      root.current = d3.select(dom) as d3.Selection<any, any, any, any>;

      // https://github.com/d3/d3-zoom/issues/46#issuecomment-235385589
      function wheeled(event: any): void {
        if (!zoom || !root.current) return;
        const dx = Math.abs(event.deltaX),
          dy = Math.abs(event.deltaY),
          t = d3.zoomTransform(root.current.node());
        if (dx > dy) {
          // horizontal scroll - translate
          zoom.translateBy(root.current, (-event.deltaX * 2) / t.k, 0);
        } else if (dx < dy) {
          // vertical scroll - zoom in/out
          // Rescale
          const k = t.k * Math.pow(2, (-event.deltaY * (event.deltaMode ? 120 : 1)) / 500);
          rescaleAndTranslate({
            parentEl: root.current,
            newZoomValue: k,
            cursorOffset: d3.pointer(event, root.current.node() as ContainerElement)[0],
          });
        }
        event.preventDefault && event.preventDefault();
      }

      // on zoom
      zoom.on('zoom', (event) => {
        // Event Transform => { k: 15.33702, x: -2360.1248900000005, y: NaN }
        setZoomTransform(event.transform);
      });

      root.current
        .attr('height', timelineComponents.scroll.offset + timelineComponents.scroll.height)
        .call(zoom)
        .on('wheel.zoom', wheeled);
      setZoomTransform(d3.zoomTransform(root.current.node()));
    },
    [rescaleAndTranslate, timelineComponents.scroll.height, timelineComponents.scroll.offset, zoom]
  );

  React.useEffect(() => {
    const onResize = (): void => {
      setWidth(root.current?.node().getBoundingClientRect().width - 1);
      setWidthMeasured(true);
    };

    // on resize - not sure when it gets triggered
    //root.on('resize', onResize);

    // on window resize
    window.addEventListener('resize', onResize, { passive: true });

    onResize();

    return (): void => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // set zoom scale
  const zoomScale = React.useMemo(
    () => (zoomTransform ? zoomTransform!.rescaleX(x) : x),
    [x, zoomTransform]
  );

  // on click of a bubble that contains multiple bubbles
  const scopedInDomain = (appearAt: number, domain: [number, number]): number => {
    if (appearAt <= domain[0]) return domain[0];
    if (appearAt > domain[1]) return domain[1];
    return appearAt;
  };

  const setZoomFocusAt = React.useCallback(
    (datum: MarkerData) => {
      const timelineDomain = domain[1] - domain[0];
      const minXDomain = timelineDomain / 30;

      const [from, to] = (function (): [number, number] {
        const paddingX =
          datum.grpEndAt - datum.grpStartAt < minXDomain
            ? 30
            : 30 + (minXDomain - (datum.grpEndAt - datum.grpStartAt) / 2);
        let from = datum.grpStartAt - paddingX;
        let to = datum.grpEndAt + paddingX;
        if (from < domain[0]) {
          from = domain[0];
          to = to + domain[0] - from;
        }
        if (to > domain[1]) {
          to = domain[1];
          from = from - (to - domain[1]);
        }
        return [from, to];
      })();

      const xDomain: [number, number] = [
        d3.zoomIdentity.rescaleX(x)(domain[0]) ?? 1,
        d3.zoomIdentity.rescaleX(x)(domain[1]) ?? 1,
      ];
      const startXOffset = scopedInDomain(d3.zoomIdentity.rescaleX(x)(from) ?? 1, xDomain);
      const endXOffset = scopedInDomain(d3.zoomIdentity.rescaleX(x)(to) ?? 1, xDomain);
      const xOffset = (endXOffset + startXOffset) / 2;
      const zoomValue = scopedInDomain(timelineDomain / (to - from), [1, 30]);
      d3.select(zoomRef.current)
        .transition()
        .duration(500)
        .call(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          zoom.transform as any,
          d3.zoomIdentity
            .translate(width / 2, 0)
            .scale(zoomValue) // zoom all the way to 100%
            .translate(-xOffset, 0)
        );
    },
    [domain, x, zoom.transform, width]
  );

  const hasZoomRefChanged = useHasChangedShallow([zoomRef.current, widthMeasured]);

  React.useEffect(() => {
    if (!hasZoomRefChanged) return;
    if (!zoomRef.current || !duration || exitDurationSecs === undefined) return;

    let scale = 1;
    let exitRoomDefaultViewSecs = exitDurationSecs ?? 0;
    let waitingRoomDefaultViewSecs = 15 * 60;

    if (props.startZoomed) {
      // Default zoom scale is total / duration + 10 minutes
      waitingRoomDefaultViewSecs = 5 * 60;
      exitRoomDefaultViewSecs = Math.min(exitDurationSecs, 5 * 60);
      scale =
        (15 * 60 + duration + exitDurationSecs) /
        (duration + waitingRoomDefaultViewSecs + exitRoomDefaultViewSecs);
    }

    const center = (duration + exitRoomDefaultViewSecs - waitingRoomDefaultViewSecs) / 2;
    const xOffset = d3.zoomIdentity.rescaleX(x)(center);
    d3.select(zoomRef.current)
      .transition()
      .duration(500)
      .call(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        zoom.transform as any,
        d3.zoomIdentity
          .translate(width / 2, 0)
          .scale(scale) // zoom all the way to 100%
          .translate(-(xOffset ?? 0), 0)
      );
  }, [
    duration,
    exitDurationSecs,
    hasZoomRefChanged,
    props.startZoomed,
    width,
    x,
    zoom.transform,
    zoomRefCallback,
    widthMeasured,
  ]);

  const min = Math.ceil(zoomScale!.domain()[0]);
  const max = Math.ceil(zoomScale!.domain()[1]);
  const onZoomScaleChange = props.onZoomScaleChange;
  const hasMinMaxChanged = useHasChangedShallow([min, max]);
  React.useEffect(() => {
    if (!hasMinMaxChanged) return;
    onZoomScaleChange && onZoomScaleChange([min, max]);
  }, [min, max, hasMinMaxChanged, onZoomScaleChange]);

  if (!zoom || !x || !zoomRefCallback) return null;

  return (
    <ZoomScaleProviderContext.Provider
      value={{
        zoom,
        x,
        zoomRef,
        zoomRefCallback,
        zoomTransform,
        width,
        zoomScale,
        setZoomFocusAt,
        rescaleAndTranslate,
      }}
    >
      {props.children}
    </ZoomScaleProviderContext.Provider>
  );
};
